import { UserOperations, UserProfile, UserRole } from '../types/user';
import { RefreshToken } from '../api/security-endpoint';

export const userInfoLocalStorageKey = 'userClaims';

export const storeUserInformation = (userInformation: UserProfile): void => {
  localStorage.setItem(userInfoLocalStorageKey, JSON.stringify(userInformation));
};

export const getUserInformation = (): string => {
  return localStorage.getItem(userInfoLocalStorageKey) || '';
};

export const cleanToken = (): void => {
  localStorage.removeItem(userInfoLocalStorageKey);
};

export const isTokenExpired = (userInformation: UserProfile): boolean => {
  if (!userInformation.AccessTokenExpiresAt) return false;
  const expNumber = Number.parseInt(userInformation.AccessTokenExpiresAt);

  const currentTime = Date.now() / 1000;
  return currentTime > expNumber;
};

export const isUserLoggedIn = (): boolean => {
  const userInformationJsonString = localStorage.getItem(userInfoLocalStorageKey) || '';
  let userInformation: UserProfile | null = null;

  try { userInformation = JSON.parse(userInformationJsonString); } 
  catch { return false }

  if (!userInformation) { return false; }

  const isExpired = isTokenExpired(userInformation);
  if (isExpired) {
    localStorage.removeItem(userInfoLocalStorageKey);
    return false;
  }

  return true;
};

export const getUserProfile = (): UserProfile | null => {
  if (!isUserLoggedIn()) return null;

  return JSON.parse(getUserInformation());
};

export const isCurrentUserAdmin = (): boolean => {
  const profile = getUserProfile();

  if (!profile) return false;

  return profile.Role.includes(UserRole.VALIDATION_PORTAL_ADMIN);
};

export const isCurrentUserKeyMaster = (): boolean =>{
  const profile = getUserProfile();

  if (!profile) return false;

  return profile.Role.includes(UserRole.SECRET_KEY_MASTER);
};

export const isDistrictUser = (): boolean => {
  const profile = getUserProfile();

  if (!profile) return false;

  return !!profile.DistrictId && !profile.SchoolId;
};

export const isSchoolUser = (): boolean => {
  const profile = getUserProfile();

  if (!profile) return false;

  return !!profile.DistrictId && !!profile.SchoolId;
};

export const canRunJobs = (): boolean => {
  const profile = getUserProfile();
  if (!profile) return false;
  return !!profile.Operations.includes(UserOperations.JOB_RUN);
};

export const canViewJobsForAnyDistrictAndSchool = (): boolean => {
  const profile = getUserProfile();
  if (!profile) return false;
  return !!profile.Operations.includes(UserOperations.CAN_VIEW_JOB_FOR_ANY_DISTRICT_OR_SCHOOL);
};

export const canRunJobsForAnyDistrictAndSchool = (): boolean => {
  const profile = getUserProfile();
  if (!profile) return false;
  return !!profile.Operations.includes(UserOperations.CAN_RUN_JOB_FOR_ANY_DISTRICT_OR_SCHOOL);
};

export const canViewCertificationsForAnyDistrictAndSchool = (): boolean => {
  const profile = getUserProfile();
  if (!profile) return false;
  return !!profile.Operations.includes(UserOperations.CAN_VIEW_CERTIFICATIONS_FOR_ANY_DISTRICT_OR_SCHOOL);
};

export const canViewJobsForAnySchoolInAssignedDistrict = (): boolean => {
  const profile = getUserProfile();
  if (!profile) return false;
  return !!profile.Operations.includes(UserOperations.CAN_VIEW_JOB_FOR_ANY_SCHOOL_IN_ASSIGNED_DISTRICT);
};

export const canRunJobsForAnySchoolInAssignedDistrict = (): boolean => {
  const profile = getUserProfile();
  if (!profile) return false;
  return !!profile.Operations.includes(UserOperations.CAN_RUN_JOB_FOR_ANY_SCHOOL_IN_ASSIGNED_DISTRICT);
};

export const canViewCertificationsForAnySchoolInAssignedDistrict = (): boolean => {
  const profile = getUserProfile();
  if (!profile) return false;
  return !!profile.Operations.includes(UserOperations.CAN_VIEW_CERTIFICATIONS_FOR_ANY_SCHOOL_IN_ASSIGNED_DISTRICT);
};

export const canCertifyData = (): boolean => {
  const profile = getUserProfile();
  if (!profile) return false;
  return !!profile.Operations.includes(UserOperations.CERTIFY_DATA);
};

export const aboutToExpire = (seconds: number): string => {
  const userProfile = getUserProfile();

  if (!userProfile) return '';

  const isExpired = isTokenExpired(userProfile);

  if (isExpired) {
    cleanToken();
    return 'expired';
  }

  const currentTime = Date.now() / 1000;
  if (Number.parseInt(userProfile.AccessTokenExpiresAt) - currentTime < seconds) return 'true';
  else return 'false';
};

export const setRefreshToken = async (): Promise<void> => {
  const seconds = parseInt(import.meta.env.VITE_APP_ABOUT_TO_LOG_OUT_FOR_INACTIVITY_IN_SECONDS || '60') + 60;
  if (aboutToExpire(seconds) === 'true') {
    RefreshToken().then(() => {});
  }
};

interface UserInfo {
  Role: string[];
  DistrictId?: string;
  SchoolId?: string;
  DistrictName?: string;
  SchoolName?: string;
  OrganizationId?: string;
  BuildingId?: string;
  Username?:string;
}

export const getUserInfo = (): UserInfo | null => {
  const userProfile = getUserProfile();

  if (!userProfile) return null;

  return {
    Role: userProfile.Role,
    DistrictId: userProfile.DistrictId || '',
    SchoolId: userProfile.SchoolId || '',
    DistrictName: userProfile.DistrictName || '',
    SchoolName: userProfile.SchoolName || '',
    OrganizationId: userProfile.OrganizationId || '',
    BuildingId: userProfile.BuildingId || '',
    Username: userProfile.Username || '',
  };
};
